import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';
import config from '../../../config';
import { Session, session } from '../../Session';
import { ErrorResponse } from './response/ErrorResponse';
import { Response } from './response/Response';
import { ResponseService } from './response/ResponseService';
import { JwtObject } from '../../session/JwtObject';
import { notifications, NotificationStore } from '../../notifications/NotificationStore';
import { ResponseStatus } from './response/ResponseStatus';
import { sipStore, SipStore } from '../sip/SipStore';
import { StorageStore } from '../../storage/StorageStore';

export class ApiService {
    private client: AxiosInstance;
    private notifications: NotificationStore;
    private sipStore?: SipStore;

    public constructor() {
        this.client = axios.create({
            baseURL: config.api.baseUrl,
            headers: {
                'Content-Type': 'application/vnd.api+json',
                Accept: 'application/vnd.api+json',
                'X-Requested-With': 'XMLHttpRequest',
            },
            responseType: 'json'
        })
        this.notifications = notifications;
        this.sipStore = sipStore;
    }

    public async request<T, E extends ErrorResponse>(
        config: AxiosRequestConfig,
        token = true
    ): Promise<Response<T> | Response<E>> {

        if (token) {
            if (!session.sessionValid) {
                session.logout();
                if (sipStore!.sipStack) {
                    sipStore!.sipStack.stop();
                }
                session.addExpiredMessage();
                return Response.make({}) as any;

            }

            if (session.usesExpired && session.isExpired()) {
                await this.refreshSession();
            }

            config = {
                ...config,
                headers: {
                    ...config.headers,
                    ...this.getAccessHeaders()
                }
            };
        }

        const response = await (new ResponseService<T, E>()).handleRequest(
            this.client.request<T>(config)
        );

        return response;
    }

    public getAccessHeaders(): { Authorisation: string } | {} {
        let tokenObject = session.jwt;

        if (tokenObject) {
            return {
                Authorization: `Bearer ${(tokenObject as JwtObject).access_token}`
            };
        }

        return {};
    }

    public getHeaders(token = true): Record<string, string> {
        return {
            ...(token ? this.getAccessHeaders() : {}),
            'Content-Type': 'application/vnd.api+json',
            Accept: 'application/vnd.api+json',
            'X-Requested-With': 'XMLHttpRequest',
        }
    }

    public post<T, E extends ErrorResponse = ErrorResponse>(
        url: string,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        data: any,
        token = true
    ): Promise<Response<T> | Response<E>> {
        return this.request<T, E>({
            method: 'post',
            url,
            data
        }, token);
    }

    public get<T, E extends ErrorResponse = ErrorResponse>(
        url: string,
        token = true
    ): Promise<Response<T> | Response<E>> {
        return this.request<T, E>({
            method: 'get',
            url
        }, token);
    }

    public put<T, E extends ErrorResponse = ErrorResponse>(
        url: string,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        data: any = {},
        token = true
    ): Promise<Response<T> | Response<E>> {
        return this.request<T, E>({
            method: 'patch',
            url,
            data
        }, token);
    }

    public delete<T, E extends ErrorResponse = ErrorResponse>(
        url: string,
        data: any = {},
        token = true
    ): Promise<Response<T> | Response<E>> {
        return this.request<T, E>({
            method: 'delete',
            url,
            data
        }, token);
    }

    public async refreshSession(): Promise<void> {
        if (session.jwt) {
            const response = await this.post<JwtObject>('oauth/token', {
                grant_type: 'refresh_token',
                refresh_token: session.jwt!.refresh_token,
                client_id: config.api.client_id,
                client_secret: config.api.secret_key,
                scope: '*'
            }, false);

            if (response.getStatus() === ResponseStatus.OK) {
                const storage = StorageStore.sessionOrLocal(Session.LS_BEARER_KEY);

                if (storage) {
                    storage.setItem(
                        Session.LS_BEARER_KEY,
                        Session.makeStoredJwt(response.getData() as JwtObject)
                    );
                }
            } else {
                throw new Error('Could not refresh token');
            }
        }
    }
}
